import * as Yup from 'yup';
import { BusinessSettings, Roles } from '@prisma/client';
import {
  Employee,
  EmployeeToBusiness,
  InputGroupType,
  InputRowType,
  LoadedUser,
  Option,
  PayoutDistributionOptions,
} from '../../interfaces';
import { OnSchedCommonType } from '../../types/on-sched';
import {
  DEFAULT_END_TIME,
  DEFAULT_START_TIME,
  EMPLOYEE_COLOR_OPTIONS,
} from '../../constants/employees';
import {
  getPayoutDistribution,
  getRoleFromSelectedRoles,
  getSelectedRolesFromEmployee,
} from '../../helpers/employees';
import {
  convertSnakeToTitleCase,
  testPhoneNumbervalue,
} from '../global/helper';
import { EmployeesRole, selectedRolesSchema } from '../../types/employees';
import { BusinessService } from '../../types/businesses';

export interface CreateArtistValues {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  displayName: string;
  urlPath: string;
  standardDesignMessage: string;
  cancellationPolicyText: string;
  liabilityWaiver: string;
  weeklyAvailability: OnSchedCommonType.Availability;
  selectedRoles: EmployeesRole[];
  about?: string;
  timezoneId: string;
  profileImage?: string;
  minAppointmentPrice?: number;
  stackDays: boolean;
  stackTimes: boolean;
  commissionPercent: string;
  isBookable: boolean;
  addToAllChats: boolean;
  generalRequestOwner: boolean;
  canViewReports: boolean;
  canManageWaivers: boolean;
  canIssueRefunds: boolean;
  canManageTeam: boolean;
  canManageCampaigns: boolean;
  canViewAllShopCustomers: boolean;
  payoutDistribution: PayoutDistributionOptions;
  color?: string;
}

export const initFormValues: CreateArtistValues = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  displayName: '',
  urlPath: '',
  standardDesignMessage: `Thanks for requesting with me! Once you schedule and pay the deposit I'll start working on the design and see you soon or let you know if I have any questions for you.`,
  cancellationPolicyText: `Deposits are non-refundable. If you need to cancel an appointment or do not show up at the scheduled time, you will not receive any portion of the deposit fee back. If you need to reschedule your appointment more than once or within 48 hours of your scheduled appointment time, you will need to pay a new deposit fee in addition to the total price of the tattoo.`,
  liabilityWaiver: '',
  weeklyAvailability: {
    sun: {
      startTime: 0,
      endTime: 0,
    },
    mon: {
      startTime: DEFAULT_START_TIME,
      endTime: DEFAULT_END_TIME,
    },
    tue: {
      startTime: DEFAULT_START_TIME,
      endTime: DEFAULT_END_TIME,
    },
    wed: {
      startTime: DEFAULT_START_TIME,
      endTime: DEFAULT_END_TIME,
    },
    thu: {
      startTime: DEFAULT_START_TIME,
      endTime: DEFAULT_END_TIME,
    },
    fri: {
      startTime: DEFAULT_START_TIME,
      endTime: DEFAULT_END_TIME,
    },
    sat: {
      startTime: 0,
      endTime: 0,
    },
  },
  selectedRoles: [EmployeesRole.ARTIST],
  about: '',
  timezoneId: '',
  profileImage: '',
  minAppointmentPrice: 0,
  stackDays: false,
  stackTimes: false,
  commissionPercent: '50%',
  isBookable: true,
  addToAllChats: false,
  generalRequestOwner: false,
  canViewReports: true,
  canManageWaivers: true,
  canIssueRefunds: true,
  canManageTeam: true,
  canManageCampaigns: true,
  canViewAllShopCustomers: true,
  payoutDistribution: PayoutDistributionOptions.EMPTY,
};

export const getInitFormValuesFromBusinessSettings = (
  businessSettings: BusinessSettings | null | undefined,
) => {
  if (!businessSettings) return initFormValues;

  return {
    ...initFormValues,
    cancellationPolicyText: businessSettings.cancellationPolicyText || '',
    minAppointmentPrice: businessSettings.minAppointmentPrice || 0,
    stackDays: businessSettings.stackDays,
    stackTimes: businessSettings.stackTimes,
  };
};

export const getInitFormValuesFromEmployee = (
  employee: Employee,
  employeeToBusiness: EmployeeToBusiness | null | undefined,
  services: BusinessService[],
): CreateArtistValues => {
  if (!employee?.user) return initFormValues;

  const payoutDistribution = getPayoutDistribution(employeeToBusiness);

  return {
    firstName: employee.user.firstName || '',
    lastName: employee.user.lastName || '',
    email: employee.user.email || '',
    phoneNumber: employee.user.phoneNumber || '',
    displayName: employee.displayName || '',
    urlPath: employee.urlPath,
    standardDesignMessage: employee.standardDesignMessage || '',
    cancellationPolicyText: employee.cancellationPolicyText || '',
    liabilityWaiver: employee.liabilityWaiver || '',
    weeklyAvailability: initFormValues.weeklyAvailability,
    selectedRoles: getSelectedRolesFromEmployee(
      employee.user?.role as Roles,
      employeeToBusiness,
      services,
    ),
    about: employee.about || '',
    timezoneId: employee.user.timezone,
    profileImage: employee.profileImage || '',
    minAppointmentPrice: employee.minAppointmentPrice || 0,
    stackDays: employee.stackDays,
    stackTimes: employee.stackTimes,
    commissionPercent: (
      (employeeToBusiness?.paymentToEmployeePercent ?? 0) * 100
    ).toString(),
    isBookable: !!employeeToBusiness?.isBookable,
    addToAllChats: !!employeeToBusiness?.addToAllChats,
    canViewReports: !!employeeToBusiness?.canViewReports,
    canManageWaivers: !!employeeToBusiness?.canManageWaivers,
    canIssueRefunds: !!employeeToBusiness?.canIssueRefunds,
    canManageTeam: !!employeeToBusiness?.canManageTeam,
    canManageCampaigns: !!employeeToBusiness?.canManageCampaigns,
    generalRequestOwner: !!employeeToBusiness?.generalRequestOwner,
    canViewAllShopCustomers: !!employeeToBusiness?.canViewAllShopCustomers,
    payoutDistribution,
    color: employeeToBusiness?.color || '',
  };
};

export const validationSchema = Yup.object().shape({
  firstName: Yup.string()
    .test({
      test: str => !!str?.trim(),
      message: 'First name is required',
    })
    .required('First name is required'),
  lastName: Yup.string()
    .test({
      test: str => !!str?.trim(),
      message: 'Last name is required',
    })
    .required('Last name is required'),
  about: Yup.string().nullable().optional(),
  email: Yup.string()
    .email('Please enter a valid email address')
    .test({
      test: value => !!value?.trim(),
      message: 'Email is required',
    })
    .required('Email is required'),
  phoneNumber: Yup.string()
    .test({
      test: testPhoneNumbervalue,
      message: 'Please enter a valid phone number',
    })
    .required('Phone number is required'),
  displayName: Yup.string()
    .test({
      test: str => !!str?.trim(),
      message: 'Display name is required',
    })
    .required('Display name is required'),
  payoutDistribution: Yup.mixed<PayoutDistributionOptions>()
    .oneOf(Object.values(PayoutDistributionOptions))
    .required('Please add a type of pay for this user'),
  commissionPercent: Yup.string().when('payoutDistribution', {
    is: PayoutDistributionOptions.COMMISSION,
    then: schema => schema.required('Commission is required'),
  }),
  urlPath: Yup.string()
    .matches(/^[a-zA-Z0-9]+(?:-[a-zA-Z0-9]+)*$/, 'Invalid URL path')
    .test({
      test: str => !!str?.trim(),
      message: 'Booking link is required',
    })
    .required('Booking link is required'),
  standardDesignMessage: Yup.string().required(
    'Standard design message is required',
  ),
  cancellationPolicyText: Yup.string().required(
    'Cancellation policy is required',
  ),
  isBookable: Yup.boolean().optional(),
  addToAllChats: Yup.boolean().optional(),
  generalRequestOwner: Yup.boolean().optional(),
  canViewReports: Yup.boolean().optional(),
  canManageWaivers: Yup.boolean().optional(),
  canManageCampaigns: Yup.boolean().optional(),
  canViewAllShopCustomers: Yup.boolean().optional(),
  selectedRoles: selectedRolesSchema,
  color: Yup.string().nullable().optional(),
});

export const roleOptions: Option[] = [
  {
    value: EmployeesRole.ARTIST,
    label: convertSnakeToTitleCase(EmployeesRole.ARTIST),
  },
  {
    value: EmployeesRole.SHOP_MANAGER,
    label: convertSnakeToTitleCase(EmployeesRole.SHOP_MANAGER),
  },
  {
    value: EmployeesRole.SHOP_BOOKER,
    label: convertSnakeToTitleCase(EmployeesRole.SHOP_BOOKER),
  },
  {
    value: EmployeesRole.PIERCER,
    label: convertSnakeToTitleCase(EmployeesRole.PIERCER),
  },
];

export const payOptions: Option[] = [
  {
    value: PayoutDistributionOptions.COMMISSION,
    label: 'Split payouts based on a commission percent',
  },
  {
    value: PayoutDistributionOptions.BUSINESS,
    label: 'Send money to the shop account',
  },
  {
    value: PayoutDistributionOptions.EMPLOYEE,
    label: 'Send money to the artist account',
  },
];

const timezoneOptions: Option[] = [
  {
    label: 'Pacific Time',
    value: 'US/Pacific',
  },
  {
    label: 'Mountain Time',
    value: 'US/Mountain',
  },
  {
    label: 'Central Time',
    value: 'US/Central',
  },
  {
    label: 'Eastern Time',
    value: 'US/Eastern',
  },
  {
    label: 'Arizona Time - MST',
    value: 'America/Phoenix',
  },
  {
    label: 'Alaskan Time',
    value: 'America/Anchorage',
  },
  {
    label: 'Hawaiian Time',
    value: 'US/Hawaii',
  },
];

const getStaffDetails = ({ employee }: { employee?: Employee | null }) => ({
  name: 'User Details',
  inputRows: [
    [
      {
        name: 'firstName',
        label: 'First Name *',
        type: 'text',
        placeholder: 'John',
      },
      {
        name: 'lastName',
        label: 'Last Name *',
        type: 'text',
        placeholder: 'Doe',
      },
    ],
    [
      {
        name: 'email',
        label: 'Email *',
        type: 'text',
        placeholder: 'john@acme.com',
      },
      {
        name: 'phoneNumber',
        label: 'Phone *',
        type: 'phone',
        placeholder: '',
      },
    ],
    [
      {
        name: 'displayName',
        label: 'Display Name *',
        type: 'text',
        placeholder: 'Dr. Woo',
      },
      {
        name: 'timezoneId',
        label: 'Timezone *',
        type: 'select',
        options: timezoneOptions,
        placeholder: 'Select a timezone',
      },
    ],
    [
      {
        name: 'urlPath',
        label: 'Booking Link *',
        type: 'link',
        placeholder: 'dr-woo',
        prependText: 'https://web.getporter.io/artist/',
        disabled: !!employee,
      },
    ],
    [
      {
        name: 'about',
        label: 'Biography',
        type: 'textarea',
        placeholder: 'Describe the artist',
      },
    ],
    [
      {
        name: 'color',
        label: 'Color',
        type: 'color',
        colors: EMPLOYEE_COLOR_OPTIONS,
        tooltip: 'The color that will be used for this user in the UI',
        // urlPath helper text
        helperText: employee
          ? "To change this user's booking link, please reach out to Porter support"
          : undefined,
      },
    ],
  ] as InputGroupType['inputRows'],
});

const profileDetails = {
  name: 'Account & Booking Settings',
  inputRows: [
    [
      {
        name: 'selectedRoles',
        label: 'Role *',
        type: 'multiselect',
        options: roleOptions,
        placeholder: 'Select a role',
        autoWidth: false,
        showListWithAll: true,
        allText: 'All Roles',
      },
    ],
    [
      {
        name: 'minAppointmentPrice',
        label: 'Minimum Price',
        type: 'dollar',
        placeholder: 'Enter amount',
      },
    ],
    [
      {
        name: 'standardDesignMessage',
        label: 'Default Reply',
        type: 'textarea',
        placeholder: '',
      },
    ],
    [
      {
        name: 'cancellationPolicyText',
        label: 'Cancellation',
        type: 'textarea',
        placeholder: '',
      },
    ],
  ],
};

const payrollDetails = {
  name: 'Payouts & Commissions',
  inputRows: [
    [
      {
        name: 'payoutDistribution',
        label: 'Payee *',
        type: 'select',
        options: payOptions,
        placeholder: 'Select a Payee',
        tooltip: "Who should receive the money for this user's appointments?",
        autoWidth: false,
      },
    ],
    [
      {
        name: 'commissionPercent',
        label: 'Commission *',
        type: 'percent',
        placeholder: 'Enter the % commission paid to the artist',
        helperText: 'Enter the % paid to the ARTIST',
        hidden: {
          start: true,
          field: 'payoutDistribution',
          changesWhen: 'COMMISSION',
        },
      },
    ],
  ],
};

export const getPermissionsDetails = ({
  values,
  currentUser,
}: {
  values: CreateArtistValues;
  currentUser: LoadedUser;
}) => {
  const isShopManager =
    getRoleFromSelectedRoles(values.selectedRoles) === 'SHOP_MANAGER';

  const shopManagerRows = isShopManager
    ? ([
        [
          {
            label: 'Add to all chats',
            name: 'addToAllChats',
            type: 'select',
            options: [
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ],
            tooltip: 'Add this user to all chats by default',
            autoWidth: false,
          },
        ],
        [
          {
            label: 'General requests',
            name: 'generalRequestOwner',
            type: 'select',
            options: [
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ],
            tooltip:
              'Allow this user to manage general requests (requests for "Any Artist")',
            autoWidth: false,
          },
        ],
        currentUser?.canViewReports && [
          {
            label: 'Can view reports',
            name: 'canViewReports',
            type: 'select',
            options: [
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ],
            tooltip: 'Allow this user to view reports',
            autoWidth: false,
          },
        ],
        currentUser?.canManageWaivers && [
          {
            label: 'Can manage waivers',
            name: 'canManageWaivers',
            type: 'select',
            options: [
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ],
            tooltip: 'Allow this user to manage waivers',
            autoWidth: false,
          },
        ],
        currentUser?.canManageCampaigns && [
          {
            label: 'Can manage campaigns',
            name: 'canManageCampaigns',
            type: 'select',
            options: [
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ],
            tooltip: 'Allow this user to manage campaigns',
          },
        ],
      ].filter(Boolean) as InputRowType[])
    : [];

  const artistRows = [
    [
      currentUser.canIssueRefunds
        ? {
            label: 'Can issue refunds',
            name: 'canIssueRefunds',
            type: 'select',
            options: [
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ],
            tooltip: 'Allow this user to issue refunds',
            autoWidth: false,
          }
        : null,
    ].filter(Boolean),
    [
      currentUser.canViewAllShopCustomers
        ? {
            label: 'Can view all customers',
            name: 'canViewAllShopCustomers',
            type: 'select',
            options: [
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ],
            tooltip: 'Allow this user to view all shop customers',
            autoWidth: false,
          }
        : null,
    ].filter(Boolean),
  ] as InputRowType[];

  const inputRows = shopManagerRows.concat(artistRows);
  if (!inputRows.length) return null;

  return {
    name: 'Permissions',
    inputRows,
  } as InputGroupType;
};

export const getInputGroups = ({
  currentUser,
  employee,
  values,
}: {
  currentUser: LoadedUser;
  employee?: Employee | null;
  values: CreateArtistValues;
}) =>
  [
    getStaffDetails({ employee }),
    payrollDetails,
    profileDetails,
    getPermissionsDetails({ values, currentUser }),
  ].filter(Boolean) as InputGroupType[];

export const getArtistFormErrors = (errorMessage: string) => {
  switch (errorMessage) {
    case 'url_path must be unique':
      return 'Booking link is already taken';
    case 'Employee already onboarded to this business':
      return 'An employee with this phone number is already onboarded to this business';
    default:
      return errorMessage;
  }
};
